import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useTheme } from '@/contexts/ThemeContext'
import {
  MODAL_PAPER_STYLES,
  MODAL_TITLE_STYLES,
  MODAL_CONTENT_STYLES,
  MODAL_BUTTON_STYLES,
} from '@/components/shared/modalStyles'
import { createTextFieldStyles } from '@/components/shared/commonStyles'
import { createDatePickerPopperStyles } from '@/components/shared/commonStyles'
import { DateRangeOption } from './types'
import TransactionTypeSelect from './TransactionTypeSelect'
import { parseLocalDate, formatLocalDate } from './utils'
import { useMemo, useState } from 'react'
import type { ElementType } from 'react'
import type { TextFieldProps } from '@mui/material'

interface ExportModalProps {
  open: boolean
  onClose: () => void
  onExport: (dateFrom: string, dateTo: string, type?: string) => void
  StyledTextField: ElementType<TextFieldProps>
  isExporting?: boolean
}

export default function ExportModal({
  open,
  onClose,
  onExport,
  StyledTextField,
  isExporting = false,
}: ExportModalProps) {
  const { colors } = useTheme()
  const [dateRange, setDateRange] = useState<DateRangeOption>(
    DateRangeOption.Last30Days
  )
  const [customDateFrom, setCustomDateFrom] = useState<string>('')
  const [customDateTo, setCustomDateTo] = useState<string>('')
  const [filterType, setFilterType] = useState<string>('')

  const datePickerPopperProps = useMemo(
    () => createDatePickerPopperStyles(colors),
    [colors]
  )

  const commonDatePickerProps = useMemo(
    () => ({
      format: 'yyyy-MM-dd' as const,
      enableAccessibleFieldDOMStructure: false,
      slots: {
        textField: StyledTextField,
      },
      slotProps: {
        textField: {
          size: 'small' as const,
          fullWidth: true,
        },
        popper: datePickerPopperProps,
      },
    }),
    [StyledTextField, datePickerPopperProps]
  )

  const getDateRange = (): { from: string; to: string } => {
    const today = new Date()
    today.setHours(23, 59, 59, 999)

    if (dateRange === DateRangeOption.Custom) {
      return {
        from: customDateFrom || '',
        to: customDateTo || '',
      }
    }

    const days = parseInt(dateRange)
    const from = new Date(today)
    from.setDate(from.getDate() - days)
    from.setHours(0, 0, 0, 0)

    return {
      from: from.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    }
  }

  const handleExport = () => {
    const { from, to } = getDateRange()
    if (from && to) {
      onExport(from, to, filterType || undefined)
    }
  }

  const canExport = () => {
    if (dateRange === DateRangeOption.Custom) {
      return customDateFrom && customDateTo
    }
    return true
  }

  const handleDateFromChange = (newValue: Date | null) => {
    setCustomDateFrom(formatLocalDate(newValue))
  }

  const handleDateToChange = (newValue: Date | null) => {
    setCustomDateTo(formatLocalDate(newValue))
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEnforceFocus
      PaperProps={{
        sx: {
          ...MODAL_PAPER_STYLES,
          backgroundColor: colors.background,
          border: `2px solid ${colors.primary}`,
          boxShadow: `0 0 20px ${colors.primaryShadow}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          ...MODAL_TITLE_STYLES,
          color: colors.primary,
          textShadow: `0 0 10px ${colors.primaryShadow}`,
        }}
      >
        Export Transactions
      </DialogTitle>
      <DialogContent>
        <Box sx={{ ...MODAL_CONTENT_STYLES, color: colors.text }}>
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: colors.text,
              opacity: 0.8,
            }}
          >
            The CSV includes transactions from the selected period and type,
            suitable for reporting. You can filter by transaction type below.
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel
              sx={{
                color: colors.text,
                '&.Mui-focused': {
                  color:
                    (colors as { yellow?: string }).yellow || colors.primary,
                },
              }}
            >
              Date range
            </InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
              label="Date range"
              sx={{
                ...createTextFieldStyles(colors),
                color: colors.primary,
                '& .MuiSelect-select': {
                  color: colors.primary,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${colors.primary} !important`,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${colors.primary} !important`,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${
                    (colors as { yellow?: string }).yellow || colors.primary
                  } !important`,
                  borderWidth: '2px',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.background,
                    border: `1px solid ${colors.primary}`,
                    '& .MuiMenuItem-root': {
                      color: colors.text,
                      '&:hover': {
                        backgroundColor: `${colors.primary}20`,
                      },
                      '&.Mui-selected': {
                        backgroundColor: `${colors.primary}33`,
                        color: colors.primary,
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value={DateRangeOption.Last30Days}>
                Last 30 days
              </MenuItem>
              <MenuItem value={DateRangeOption.Last6Months}>
                Last 6 months
              </MenuItem>
              <MenuItem value={DateRangeOption.Last12Months}>
                Last 12 months
              </MenuItem>
              <MenuItem value={DateRangeOption.Custom}>Custom</MenuItem>
            </Select>
          </FormControl>

          {dateRange === DateRangeOption.Custom && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DatePicker
                  label="From"
                  value={parseLocalDate(customDateFrom)}
                  onChange={handleDateFromChange}
                  {...commonDatePickerProps}
                />
                <DatePicker
                  label="To"
                  value={parseLocalDate(customDateTo)}
                  onChange={handleDateToChange}
                  {...commonDatePickerProps}
                />
              </Box>
            </LocalizationProvider>
          )}

          <TransactionTypeSelect
            value={filterType}
            onChange={setFilterType}
            label="Transaction Type"
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={isExporting}
          sx={{
            ...MODAL_BUTTON_STYLES,
            color: colors.primary,
            borderColor: colors.primary,
            border: '1px solid',
            '&:hover': {
              backgroundColor: colors.primary,
              color: colors.background,
            },
            '&:disabled': {
              opacity: isExporting ? 1 : 0.5,
              color: colors.primary,
              borderColor: colors.primary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={!canExport() || isExporting}
          sx={{
            ...MODAL_BUTTON_STYLES,
            backgroundColor: colors.primary,
            color: colors.background,
            minWidth: 130,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            '&:hover': {
              backgroundColor: colors.primary,
              opacity: 0.9,
            },
            '&:disabled': {
              opacity: isExporting ? 1 : 0.5,
              backgroundColor: isExporting ? colors.primary : undefined,
              color: colors.background,
            },
          }}
        >
          {isExporting && (
            <CircularProgress
              size={18}
              sx={{ color: colors.background }}
              data-testid="export-spinner"
            />
          )}
          {isExporting ? 'Exporting' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
